/**
 * Client-side analytics tracker.
 * Tracks visitors, sessions, page views, events, and conversions.
 * Sends data to the analytics-ingest edge function AND to GA4 via gtag.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

const GA4_ID = "G-CQ0X584G49";

function sendGA4Event(eventName: string, params?: Record<string, any>) {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

const VISITOR_KEY = "usa_vid";
const SESSION_KEY = "usa_sid";
const SESSION_START_KEY = "usa_sstart";
const SESSION_SENT_KEY = "usa_ssent"; // tracks if session_start was sent to server
const SESSION_ATTRIBUTION_KEY = "usa_attr"; // persisted source/medium/campaign for the session
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 min

let scrollMax = 0;
let engagementStart = Date.now();
let lastActivityAt = Date.now();
let accumulatedEngagementMs = 0;
let isVisible = true;
let pageViewCount = 0;
let eventBuffer: any[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initBuffer: Array<{ type: string; data: any }> = [];
let initFlushTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

const HEARTBEAT_MS = 15000; // push engagement update every 15s
const IDLE_THRESHOLD_MS = 30000; // user considered idle after 30s no activity

function generateId(): string {
  return crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Accumulate elapsed engagement since last sample, but only while page is visible AND user not idle. */
function sampleEngagement() {
  const now = Date.now();
  const since = now - engagementStart;
  const idleFor = now - lastActivityAt;
  if (isVisible && idleFor < IDLE_THRESHOLD_MS && since > 0) {
    accumulatedEngagementMs += since;
  }
  engagementStart = now;
}

function getVisitorId(): string {
  let vid = localStorage.getItem(VISITOR_KEY);
  if (!vid) {
    vid = generateId();
    localStorage.setItem(VISITOR_KEY, vid);
  }
  return vid;
}

function getSessionId(): string {
  const lastActivity = sessionStorage.getItem(SESSION_START_KEY);
  const sid = sessionStorage.getItem(SESSION_KEY);

  if (sid && lastActivity && Date.now() - parseInt(lastActivity) < SESSION_TIMEOUT) {
    sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
    return sid;
  }

  // New session
  const newSid = generateId();
  sessionStorage.setItem(SESSION_KEY, newSid);
  sessionStorage.setItem(SESSION_START_KEY, Date.now().toString());
  return newSid;
}

function isNewSession(): boolean {
  const sid = sessionStorage.getItem(SESSION_KEY);
  const sent = sessionStorage.getItem(SESSION_SENT_KEY);
  const lastActivity = sessionStorage.getItem(SESSION_START_KEY);
  
  // New session if: no session exists, session was never sent to server, or session timed out
  if (!sid || !sent) return true;
  if (lastActivity && Date.now() - parseInt(lastActivity) >= SESSION_TIMEOUT) return true;
  return false;
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let device_type = "desktop";
  if (/Mobi|Android/i.test(ua)) device_type = "mobile";
  else if (/Tablet|iPad/i.test(ua)) device_type = "tablet";

  let browser = "unknown";
  if (/Chrome/i.test(ua) && !/Edge|OPR/i.test(ua)) browser = "Chrome";
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Edge/i.test(ua)) browser = "Edge";
  else if (/OPR|Opera/i.test(ua)) browser = "Opera";

  let os = "unknown";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad/i.test(ua)) os = "iOS";

  return { device_type, browser, os, user_agent: ua };
}

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_term: params.get("utm_term") || undefined,
    utm_content: params.get("utm_content") || undefined,
    gclid: params.get("gclid") || undefined,
    fbclid: params.get("fbclid") || undefined,
  };
}

function classifyChannel(utmSource?: string, utmMedium?: string, referrer?: string, gclid?: string, fbclid?: string) {
  if (gclid) return { source: "google", medium: "cpc", channel: "paid_search" };
  if (fbclid) return { source: "facebook", medium: "cpc", channel: "paid_social" };
  if (utmMedium === "cpc" || utmMedium === "ppc") return { source: utmSource || "unknown", medium: utmMedium, channel: "paid_search" };
  if (utmSource) return { source: utmSource, medium: utmMedium || "unknown", channel: utmMedium === "email" ? "email" : utmMedium === "social" ? "social" : "campaign" };
  if (!referrer || referrer === "") return { source: "direct", medium: "none", channel: "direct" };
  if (/google\.|bing\.|yahoo\.|duckduckgo\./i.test(referrer)) return { source: new URL(referrer).hostname.replace("www.", ""), medium: "organic", channel: "organic_search" };
  if (/facebook\.|instagram\.|twitter\.|linkedin\.|tiktok\./i.test(referrer)) return { source: new URL(referrer).hostname.replace("www.", ""), medium: "social", channel: "organic_social" };
  return { source: new URL(referrer).hostname.replace("www.", ""), medium: "referral", channel: "referral" };
}

function getCityCluster(): string {
  const path = window.location.pathname;
  if (path.startsWith("/rio-de-janeiro")) return "rio-de-janeiro";
  return "brasilia";
}

function getRouteLayer(): string {
  const path = window.location.pathname;
  if (path === "/") return "home";
  if (path.startsWith("/servicos/")) return "service";
  if (path.startsWith("/brasilia/") || path.startsWith("/rio-de-janeiro/")) {
    const parts = path.split("/").filter(Boolean);
    if (parts.length === 3) return "bairro_service";
    if (parts.length === 2) return "bairro";
    return "city_hub";
  }
  if (path.startsWith("/blog")) return "blog";
  if (path === "/contato") return "contato";
  if (path === "/diferencial") return "diferencial";
  if (path === "/quem-somos") return "quem-somos";
  return "other";
}

function getServiceSlug(): string | undefined {
  const path = window.location.pathname;
  const match = path.match(/\/servicos\/([^/]+)/);
  if (match) return match[1];
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 3 && (parts[0] === "brasilia" || parts[0] === "rio-de-janeiro")) return parts[2];
  return undefined;
}

function getBairroSlug(): string | undefined {
  const path = window.location.pathname;
  const parts = path.split("/").filter(Boolean);
  if (parts.length >= 2 && (parts[0] === "brasilia" || parts[0] === "rio-de-janeiro")) return parts[1];
  return undefined;
}

const INGEST_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analytics-ingest`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function sendToIngest(type: string, data: any) {
  // Buffer all calls during init and send as single request
  initBuffer.push({ type, data });
  if (initFlushTimer) clearTimeout(initFlushTimer);
  initFlushTimer = setTimeout(flushInitBuffer, 50);
}

/** Pick the best transport: sendBeacon survives page unloads / tab switches (e.g. wa.me clicks). */
function postPayload(payload: string, useBeacon: boolean): boolean {
  if (useBeacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      // sendBeacon ignores custom headers; the edge function accepts anon via URL? not allowed.
      // Fallback to fetch keepalive when beacon needs auth headers.
      // We'll try beacon with a Blob; if the function rejects (no Authorization), fetch keepalive runs as backup.
      const blob = new Blob([payload], { type: "application/json" });
      const ok = navigator.sendBeacon(`${INGEST_URL}?apikey=${encodeURIComponent(ANON_KEY)}`, blob);
      if (ok) return true;
    } catch {
      // fall through to fetch
    }
  }
  try {
    fetch(INGEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body: payload,
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

function flushInitBuffer(useBeacon = false) {
  if (initBuffer.length === 0) return;
  const items = [...initBuffer];
  initBuffer = [];

  const payload = items.length === 1
    ? JSON.stringify(items[0])
    : JSON.stringify({ type: "multi", data: { items } });

  postPayload(payload, useBeacon);
}

function bufferEvent(event: any) {
  eventBuffer.push(event);
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushEvents, 3000);
}

function flushEvents(useBeacon = false) {
  if (eventBuffer.length === 0) return;
  const batch = [...eventBuffer];
  eventBuffer = [];
  // Push to init buffer so it goes out in the same payload when possible
  initBuffer.push({ type: "batch", data: { events: batch } });
  if (useBeacon) {
    if (initFlushTimer) { clearTimeout(initFlushTimer); initFlushTimer = null; }
    flushInitBuffer(true);
  } else {
    if (initFlushTimer) clearTimeout(initFlushTimer);
    initFlushTimer = setTimeout(flushInitBuffer, 50);
  }
}

// ======= PUBLIC API =======

export function initTracker() {
  if (typeof window === "undefined") return;

  // Don't track Lovable preview
  const host = window.location.hostname;
  // Block tracking only on preview/dev environments, NOT on the published site
  const isPreview = host.includes("id-preview--") || host.includes("lovable.dev") || host === "localhost";
  if (isPreview) return;
  const visitorId = getVisitorId();
  const newSession = isNewSession();
  const sessionId = getSessionId();
  const device = getDeviceInfo();

  // Register/update visitor
  sendToIngest("visitor", {
    visitor_id: visitorId,
    ...device,
  });

  // Start session if new
  if (newSession) {
    // Clear old session data to force fresh start
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_START_KEY);
    sessionStorage.removeItem(SESSION_SENT_KEY);
    const freshSessionId = getSessionId();
    
    const utms = getUTMParams();
    const channelInfo = classifyChannel(utms.utm_source, utms.utm_medium, document.referrer, utms.gclid, utms.fbclid);

    // Persist attribution for the whole session so conversions can carry real source/medium/campaign
    try {
      sessionStorage.setItem(SESSION_ATTRIBUTION_KEY, JSON.stringify({
        source: channelInfo.source,
        medium: channelInfo.medium,
        channel: channelInfo.channel,
        campaign: utms.utm_campaign || null,
        gclid: utms.gclid || null,
        fbclid: utms.fbclid || null,
        landing_path: window.location.pathname,
      }));
    } catch {}

    sendToIngest("session_start", {
      session_id: freshSessionId,
      visitor_id: visitorId,
      landing_page: window.location.href,
      landing_path: window.location.pathname,
      ...utms,
      ...channelInfo,
      city_cluster: getCityCluster(),
    });
    
    // Mark session as sent to server
    sessionStorage.setItem(SESSION_SENT_KEY, "1");
  }

  // Track page view (this also increments local counter & flushes a session_update)
  trackPageView();

  // Reset engagement clock for this page
  scrollMax = 0;
  accumulatedEngagementMs = 0;
  engagementStart = Date.now();
  lastActivityAt = Date.now();
  isVisible = document.visibilityState === "visible";

  // Scroll tracking
  window.addEventListener("scroll", () => {
    lastActivityAt = Date.now();
    const scrollPercent = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    );
    if (scrollPercent > scrollMax) scrollMax = scrollPercent;
  }, { passive: true });

  // Activity signals (mouse, keyboard, touch)
  const markActive = () => { lastActivityAt = Date.now(); };
  window.addEventListener("mousemove", markActive, { passive: true });
  window.addEventListener("keydown", markActive, { passive: true });
  window.addEventListener("touchstart", markActive, { passive: true });
  window.addEventListener("click", markActive, { passive: true });

  // Heartbeat: push partial session_update every 15s so engagement isn't lost on tab switch.
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = setInterval(() => {
    sampleEngagement();
    if (accumulatedEngagementMs <= 0) return;
    sendToIngest("session_update", {
      session_id: sessionId,
      exit_page: window.location.href,
      scroll_max_percent: scrollMax,
      engagement_time_ms: accumulatedEngagementMs,
      pages_viewed: pageViewCount,
      engaged: accumulatedEngagementMs > 10000 || scrollMax > 50,
      // Bounce só após o "fim" — durante a sessão consideramos não-bounce assim que houver engajamento real.
      is_bounce: pageViewCount <= 1 && scrollMax < 25 && accumulatedEngagementMs < 10000,
    });
  }, HEARTBEAT_MS);

  // Final flush on unload — uses sendBeacon to survive wa.me / tab close.
  const handleUnload = () => {
    sampleEngagement();
    sendToIngest("session_update", {
      session_id: sessionId,
      exit_page: window.location.href,
      scroll_max_percent: scrollMax,
      engagement_time_ms: accumulatedEngagementMs,
      pages_viewed: pageViewCount,
      engaged: accumulatedEngagementMs > 10000 || scrollMax > 50,
      is_bounce: pageViewCount <= 1 && scrollMax < 25 && accumulatedEngagementMs < 10000,
      ended_at: new Date().toISOString(),
    });
    flushEvents(true); // beacon mode
  };

  window.addEventListener("pagehide", handleUnload);
  window.addEventListener("beforeunload", handleUnload);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      isVisible = false;
      sampleEngagement();
      handleUnload();
    } else {
      isVisible = true;
      engagementStart = Date.now();
      lastActivityAt = Date.now();
    }
  });
}

export function trackPageView() {
  const visitorId = getVisitorId();
  const sessionId = getSessionId();
  pageViewCount += 1;

  bufferEvent({
    session_id: sessionId,
    visitor_id: visitorId,
    event_name: "page_view",
    event_category: "navigation",
    event_action: "view",
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    city_cluster: getCityCluster(),
    route_layer: getRouteLayer(),
    service_slug: getServiceSlug(),
    bairro_slug: getBairroSlug(),
  });

  // Send page_view to GA4
  sendGA4Event("page_view", {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    send_to: GA4_ID,
  });

  // Push absolute pages_viewed counter (UPDATE-overwrite is safe because we always send the latest count)
  sendToIngest("session_update", {
    session_id: sessionId,
    pages_viewed: pageViewCount,
  });
}

export function trackAnalyticsEvent(eventName: string, extra?: Record<string, any>) {
  const visitorId = getVisitorId();
  const sessionId = getSessionId();

  bufferEvent({
    session_id: sessionId,
    visitor_id: visitorId,
    event_name: eventName,
    event_category: extra?.category || "interaction",
    event_action: extra?.action || eventName,
    event_label: extra?.label,
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    city_cluster: getCityCluster(),
    route_layer: getRouteLayer(),
    service_slug: getServiceSlug(),
    bairro_slug: getBairroSlug(),
    value_numeric: extra?.value,
    metadata_json: extra?.metadata,
  });
}

export function trackConversion(conversionType: string, extra?: Record<string, any>) {
  if (typeof window === "undefined") return;

  console.log("[Analytics] trackConversion called:", conversionType, extra);

  const visitorId = getVisitorId();
  const sessionId = getSessionId();

  // Recover real attribution captured at session start (gclid, source, medium, campaign).
  // Falls back to current URL params if storage was cleared.
  let attribution: any = {};
  try {
    const raw = sessionStorage.getItem(SESSION_ATTRIBUTION_KEY);
    if (raw) attribution = JSON.parse(raw);
  } catch {}
  if (!attribution.source) {
    const utms = getUTMParams();
    const ch = classifyChannel(utms.utm_source, utms.utm_medium, document.referrer, utms.gclid, utms.fbclid);
    attribution = {
      source: ch.source,
      medium: ch.medium,
      channel: ch.channel,
      campaign: utms.utm_campaign || null,
      gclid: utms.gclid || null,
      fbclid: utms.fbclid || null,
    };
  }

  // The "source" field passed by callers is actually the CTA location (floating, mobile_sticky...).
  // Preserve it inside metadata, while filling source/medium/campaign with REAL traffic attribution.
  const ctaLocation = extra?.source;

  const conversionEvent = {
    session_id: sessionId,
    visitor_id: visitorId,
    event_name: `conversion_${conversionType}`,
    event_category: "conversion",
    event_action: conversionType,
    event_label: ctaLocation,
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
    city_cluster: getCityCluster(),
    route_layer: getRouteLayer(),
    service_slug: getServiceSlug(),
    bairro_slug: getBairroSlug(),
  };

  const conversionRow = {
    session_id: sessionId,
    visitor_id: visitorId,
    conversion_type: conversionType,
    conversion_value: extra?.value,
    page_path: window.location.pathname,
    city_cluster: getCityCluster(),
    service_slug: getServiceSlug(),
    bairro_slug: getBairroSlug(),
    source: attribution.source || "direct",
    medium: attribution.medium || "none",
    campaign: attribution.campaign || null,
    metadata_json: {
      cta_location: ctaLocation,
      channel: attribution.channel || null,
      gclid: attribution.gclid || null,
      fbclid: attribution.fbclid || null,
      ...(extra?.metadata || {}),
    },
  };

  // Snapshot engagement BEFORE the user navigates away (wa.me, tel:).
  sampleEngagement();
  const sessionUpdate = {
    session_id: sessionId,
    exit_page: window.location.href,
    scroll_max_percent: scrollMax,
    engagement_time_ms: accumulatedEngagementMs,
    pages_viewed: pageViewCount,
    engaged: true,
    is_bounce: false,
  };

  // === CRITICAL: send EVERYTHING in a single multi payload via beacon ===
  // Cancel any pending timers and bypass the buffer to avoid losing data on navigation.
  if (initFlushTimer) { clearTimeout(initFlushTimer); initFlushTimer = null; }
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }

  // Drain any previously-buffered items so they ship together.
  const previousInit = [...initBuffer];
  const previousEvents = [...eventBuffer];
  initBuffer = [];
  eventBuffer = [];

  const items: Array<{ type: string; data: any }> = [
    ...previousInit,
    { type: "event", data: conversionEvent },
    { type: "conversion", data: conversionRow },
    { type: "session_update", data: sessionUpdate },
  ];
  if (previousEvents.length > 0) {
    items.push({ type: "batch", data: { events: previousEvents } });
  }

  const payload = JSON.stringify({ type: "multi", data: { items } });

  // Redundant delivery: beacon (survives unload) + fetch keepalive (better delivery guarantee).
  // Using BOTH maximizes the chance the conversion lands even if WhatsApp app suspends the tab.
  let beaconOk = false;
  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      beaconOk = navigator.sendBeacon(`${INGEST_URL}?apikey=${encodeURIComponent(ANON_KEY)}`, blob);
    } catch {}
  }
  try {
    fetch(INGEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {}

  // Send conversion to GA4
  const ga4EventName = conversionType === "whatsapp" ? "whatsapp_click"
    : conversionType === "phone" ? "phone_click"
    : conversionType === "form" ? "form_submit"
    : `conversion_${conversionType}`;

  sendGA4Event(ga4EventName, {
    event_category: "conversion",
    event_label: ctaLocation,
    page_path: window.location.pathname,
    city_cluster: getCityCluster(),
    service_slug: getServiceSlug(),
    send_to: GA4_ID,
  });

  console.log("[Analytics] conversion sent (beacon=" + beaconOk + ")", conversionRow);
}
